import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Popper,
  Grid,
} from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useFetchSystem from "../../hooks/useFetchSystem";

interface System {
  id: number;
  name: string;
  description?: string;
  type?: string;
}

interface AssignSystemProps {
  selectedSystems: number[];
  onSystemChange: (systemIds: number[]) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  viewMode?: boolean;
  assignedSystems?: any[];
}

const AssignSystem: React.FC<AssignSystemProps> = ({
  selectedSystems,
  onSystemChange,
  disabled = false,
  loading = false,
  error = "",
  label = "Assign Systems",
  placeholder = "Select...",
  viewMode = false,
  assignedSystems = [],
}) => {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [systemData] = useFetchSystem({ setIsLoading });

  useEffect(() => {
    console.log(systemData);
  }, [systemData]);

  useEffect(() => {
    setSelectedIds(selectedSystems);
  }, [selectedSystems]);

  const handleChange = (_event: any, newValue: any) => {
    const newSelectedSystemIds = newValue.map(
      (system: { id: any }) => system.id
    );
    setSelectedIds(newSelectedSystemIds);
    onSystemChange(newSelectedSystemIds);
  };

  const renderSystemsPopper = (props: any) => (
    <Popper
      {...props}
      className="filter-autocomplete-zindex"
      anchorEl={document.getElementById("assign-systems-autocomplete")}
    />
  );

  // View mode rendering
  if (viewMode) {
    return (
      <Box className="text-box-card-white" sx={{ mt: 2 }}>
        <Box className="text-box-header">
          <h3>{label}</h3>
        </Box>
        <Grid container rowSpacing={1.5} columnSpacing={2.5}>
          <Grid item xs={12}>
            <label className="label-text">Systems</label>
            <Box sx={{ mt: 1 }}>
              {assignedSystems && assignedSystems.length > 0 ? (
                <div className="tags-tooltip-parent">
                  {assignedSystems.map((system: any, index: number) => (
                    <Box className="chip" key={system.id}>
                      <Typography variant="body2" fontWeight="medium">
                        {systemData.find((sys) => sys.id === system.system_id)
                          ?.value || ""}
                      </Typography>
                    </Box>
                  ))}
                </div>
              ) : (
                <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  No systems assigned
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      <div className="form-group non-flex">
        <div className="label-text">{label}</div>
        <div className="form-input">
          <Autocomplete
            id="assign-systems-autocomplete"
            PopperComponent={renderSystemsPopper}
            fullWidth={true}
            className="form-control-autocomplete"
            multiple
            options={systemData || []}
            getOptionLabel={(option: any) => option?.value || ""}
            disableCloseOnSelect
            disabled={disabled}
            onChange={handleChange}
            renderInput={(params) => (
              <>
                <div className="autocomplete-chips-direction">
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>
              </>
            )}
            renderTags={(_value, getTagProps) => {
              const selectedSystemObjects = selectedIds
                .map((systemId: number) =>
                  systemData.find((system) => system.id === systemId)
                )
                .filter(
                  (system: System | undefined): system is System =>
                    system !== undefined
                );

              return (
                <div className="align-from-left">
                  {selectedSystemObjects.map((option: any, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={option?.value}
                      sx={{
                        backgroundColor: theme.palette.info.light,
                        color: theme.palette.info.contrastText,
                        "& .MuiChip-deleteIcon": {
                          color: theme.palette.info.contrastText,
                        },
                      }}
                    />
                  ))}
                </div>
              );
            }}
            renderOption={(props, option: any, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.name}`}
                value={option?.name}
                sx={{ justifyContent: "space-between" }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option?.value}
                  </Typography>
                  {option?.description && (
                    <Typography variant="caption" color="textSecondary">
                      {option?.description}
                    </Typography>
                  )}
                </Box>
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )}
            value={selectedIds
              .map((systemId: number) =>
                systemData.find((system) => system.id === systemId)
              )
              .filter(
                (system: System | undefined): system is System =>
                  system !== undefined
              )}
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
          />
        </div>
      </div>
    </>
  );
};

export default AssignSystem;
